import moment from 'moment'
import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import Block from 'components/ui/block'
import FadeIn from 'components/ui/fadeIn'
import Breakdown from 'components/ui/breakdown'
import React, { useEffect, useMemo } from 'react'
import { useStokePosts } from 'hooks/useStokePosts'
import { getLayoutPattern, Icon } from 'components/ui'
import { StokrLoader } from '@stokr/components-library'
import { PageMeta, SITE_BASE_URL } from 'components/PageMeta'
import BaseLayout from 'components/layouts/baseLayout/BaseLayout'
import { RelatedArticlesSection } from './StokePostArticle.styles'
import { BlogPost, Button, Copy, t } from '@stokr/components-library'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { BlogGridLayout } from 'components/ui/stokeposts/StokePostList.styles'
import { PostContainer, BodyContainer, VideoContainer } from 'components/stoke-post/post/Components'
import {
  cmsText,
  isPressRelease,
  articleSeoTitle,
  articleSeoImage,
  articleSeoDescription,
  toBlogPostingStructuredData,
} from 'models/stokePost'
import {
  SmallIntroHomepage,
  SmallIntroHomepageImg,
  SmallIntroHomepageUrl,
} from 'components/blocks/smallIntro/SmallIntroHomepage'

const FULL_HTML_DOCUMENT_REGEX = /^<!doctype|^<html/i

const extractHtmlDocumentParts = (html = '') => {
  if (!FULL_HTML_DOCUMENT_REGEX.test(html.trimStart())) {
    return null
  }

  // No DOMParser during SSR: cut out the same parts with regexes, so the newsletter's own
  // <head> (with its <title>) isn't rendered into the page body.
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return {
      bodyHtml: html.match(/<body[^>]*>([\s\S]*)<\/body>/i)?.[1] ?? html,
      styles: [...html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)].map((m) => m[1]).filter(Boolean),
      links: [...html.matchAll(/<link\b[^>]*rel=["']stylesheet["'][^>]*>/gi)]
        .map((m) => m[0].match(/href=["']([^"']+)["']/i)?.[1])
        .filter(Boolean),
    }
  }

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const bodyHtml = doc.body?.innerHTML || html
    const styles = Array.from(doc.querySelectorAll('style'))
      .map((styleTag) => styleTag.textContent)
      .filter(Boolean)
    const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"][href]'))
      .map((linkTag) => linkTag.getAttribute('href'))
      .filter(Boolean)

    return {
      bodyHtml,
      styles,
      links,
    }
  } catch (_) {
    return {
      bodyHtml: html,
      styles: [],
      links: [],
    }
  }
}

const StokePostArticle = () => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const { url } = useParams()

  const { data, isPending, isError, error, isFetching } = useStokePosts({
    variant: 'article',
    url: url ?? '',
  })

  const post = data?.post
  const relatedPosts = data?.relatedPosts ?? []
  const isLoading = isPending || (isFetching && !post)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [url])

  useEffect(() => {
    if (post?.status === 'Inactive') {
      navigate('/page-not-found')
    }
  }, [post?.status, navigate])

  useEffect(() => {
    if (!isError) return
    const status = error?.status ?? error?.response?.status
    if (status === 404) {
      navigate('/page-not-found')
    }
  }, [isError, error, navigate])

  const { readingtime, title, description, video_url, video_position, publish_date, category } = post ?? {}

  // Canonical address — the CMS meta_url holds slugs, staging links and image URLs.
  const articleUrl = `${SITE_BASE_URL}/stoke-post/${post?.url ?? url}`

  const embeddedHtmlDocument = useMemo(() => {
    const extracted = extractHtmlDocumentParts(description || '')
    if (!extracted) return null
    const bodyHtml = typeof window !== 'undefined' ? DOMPurify.sanitize(extracted.bodyHtml) : extracted.bodyHtml
    return { ...extracted, bodyHtml }
  }, [description])

  return (
    <BaseLayout>
      <>
        {isLoading || !post ? (
          <StokrLoader noFullscreen />
        ) : (
          <>
            <PageMeta
              title={articleSeoTitle(post)}
              description={articleSeoDescription(post)}
              image={articleSeoImage(post)}
              path={post.url ? `/stoke-post/${post.url}` : undefined}
              type="article"
              publishedTime={cmsText(publish_date)}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: toBlogPostingStructuredData(post, articleUrl) }}
            />
            <FadeIn delay={80}>
              <PostContainer>
                <Block noBorder noContainerPadding={true} compact>
                  <SmallIntroHomepage
                    title={title}
                    readingtime={readingtime ? t('stokePost.article.minRead', { readingtime }) : ''}
                    compact
                    last
                    category={category}
                    isPressRelease={isPressRelease(post)}
                    withReturnButton
                    withBlueBottomBar
                    date={publish_date}
                    fullUrl={articleUrl}
                    selectedCategory={state?.selectedCategory}>
                    {video_url && video_position === 'top' && (
                      <SmallIntroHomepageImg>
                        <VideoContainer marginBottom>
                          <iframe
                            width="560"
                            title={video_url}
                            height="315"
                            src={video_url}
                            sandbox="allow-scripts allow-presentation allow-popups"
                            frameBorder="0"
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                            style={{ width: '100%' }}
                          />
                        </VideoContainer>
                      </SmallIntroHomepageImg>
                    )}
                    {embeddedHtmlDocument ? (
                      <>
                        {embeddedHtmlDocument.styles.map((style, index) => (
                          <style key={`embedded-html-style-${index}`} dangerouslySetInnerHTML={{ __html: style }} />
                        ))}
                        {embeddedHtmlDocument.links.map((href, index) => (
                          <link key={`embedded-html-link-${index}`} rel="stylesheet" href={href} />
                        ))}
                        <BodyContainer>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: embeddedHtmlDocument.bodyHtml,
                            }}
                          />
                        </BodyContainer>
                      </>
                    ) : (
                      <BodyContainer>{parse(description || '')}</BodyContainer>
                    )}

                    {video_url && video_position !== 'top' && (
                      <SmallIntroHomepageImg>
                        <VideoContainer marginTop>
                          <iframe
                            width="560"
                            title={video_url}
                            height="315"
                            src={video_url}
                            frameBorder="0"
                            sandbox="allow-scripts allow-presentation allow-popups"
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                            style={{ width: '100%' }}
                          />
                        </VideoContainer>
                      </SmallIntroHomepageImg>
                    )}
                  </SmallIntroHomepage>
                  {relatedPosts.length > 0 && (
                    <RelatedArticlesSection>
                      <Breakdown style={{ marginBottom: '1rem' }}>
                        <Copy id="stokePost.article.stokePostLabel" />
                      </Breakdown>
                      <h2>
                        <Copy id="stokePost.article.discoverRelatedInsights" />
                      </h2>
                      <SmallIntroHomepageUrl
                        to={{
                          pathname: '/stoke-post',
                        }}
                        state={{
                          selectedCategory: state?.selectedCategory,
                        }}
                        left
                        style={{
                          marginBottom: 32,
                          marginTop: 48,
                          width: '100%',
                        }}>
                        <Button>
                          <Copy id="stokePost.article.exploreAllPosts" />{' '}
                          <Icon
                            name={'ArrowSimple'}
                            style={{ marginLeft: 12, marginRight: -12 }}
                            direction={'right'}
                          />{' '}
                        </Button>
                      </SmallIntroHomepageUrl>
                      <BlogGridLayout>
                        {/* BlogPost renders no anchor; the Link makes each card a crawlable link (as in StokePostList). */}
                        {relatedPosts.map((_post, index) => (
                          <Link
                            key={_post.url}
                            style={{ opacity: 1, display: 'contents' }}
                            to={`/stoke-post/${_post.url}`}
                            state={{ selectedCategory: state?.selectedCategory }}>
                            <BlogPost
                              description={_post.meta_description}
                              image={_post.image}
                              category={_post.category}
                              title={_post.thumbnail_title}
                              date={moment(_post.publish_date).format('DD MMM YYYY')}
                              withHoverBg
                              // The Link navigates; no-op stops BlogPost's own reload (see StokePostList).
                              onClick={() => {}}
                              link={`/stoke-post/${_post.url}`}
                              layout={getLayoutPattern(relatedPosts.length === 4 ? index : index + 4)}
                            />
                          </Link>
                        ))}
                      </BlogGridLayout>
                    </RelatedArticlesSection>
                  )}
                </Block>
              </PostContainer>
            </FadeIn>
          </>
        )}
      </>
    </BaseLayout>
  )
}

export default StokePostArticle
